import React, { useMemo, useState } from 'react';
import { buildUiHelpers } from './sharedUi';

function sentimentColor(tone, COLORS) {
  if (tone === 'red') return COLORS.red;
  if (tone === 'orange') return COLORS.orange || '#f59e0b';
  if (tone === 'yellow') return COLORS.yellow || '#facc15';
  return COLORS.green;
}

function isLightTheme(COLORS) {
  return String(COLORS.card || '').toLowerCase() === '#ffffff';
}

function panelBg(COLORS) {
  return isLightTheme(COLORS) ? '#f7fbfd' : '#ffffff06';
}

function chartBg(COLORS) {
  return isLightTheme(COLORS) ? '#f8fcfd' : '#ffffff05';
}

function trackBg(COLORS) {
  return isLightTheme(COLORS) ? '#dbe7ed' : '#ffffff12';
}

function gridStroke(COLORS) {
  return isLightTheme(COLORS) ? '#9eb5c255' : '#ffffff22';
}

function weakGridStroke(COLORS) {
  return isLightTheme(COLORS) ? '#9eb5c23d' : '#ffffff18';
}

function formatSentimentValue(component) {
  if (!component) return '–';
  const unit = component.unit ? ` ${component.unit}` : '';
  return `${component.value ?? '–'}${unit}`;
}

function formatMarketSentimentStand(marketSentiment) {
  const dateText = marketSentiment?.as_of || '–';
  const timestamp = marketSentiment?.calculated_at || marketSentiment?.timestamp;
  if (!timestamp) return dateText;
  const rawTimestamp = String(timestamp);
  const normalizedTimestamp = /(?:Z|[+-]\d{2}:?\d{2})$/.test(rawTimestamp)
    ? rawTimestamp
    : `${rawTimestamp}Z`;
  const parsed = new Date(normalizedTimestamp);
  if (!Number.isNaN(parsed.getTime())) {
    const timeText = new Intl.DateTimeFormat('de-DE', {
      timeZone: 'Europe/Berlin',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(parsed);
    return `${dateText} · ${timeText} Uhr`;
  }
  const match = rawTimestamp.match(/T(\d{2}:\d{2})/);
  return match ? `${dateText} · ${match[1]} Uhr` : dateText;
}

function InfoHint({ text, COLORS }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', marginLeft: '6px', verticalAlign: 'middle' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="Erklärung anzeigen"
        style={{
          width: '16px',
          height: '16px',
          borderRadius: '999px',
          border: `1px solid ${COLORS.border}`,
          background: '#ffffff10',
          color: COLORS.muted,
          fontSize: '11px',
          fontWeight: 900,
          lineHeight: '14px',
          padding: 0,
          cursor: 'help',
        }}
      >
        i
      </button>
      {open && (
        <span
          role="tooltip"
          style={{
            position: 'absolute',
            zIndex: 20,
            left: '50%',
            bottom: '22px',
            width: '260px',
            transform: 'translateX(-50%)',
            padding: '10px 11px',
            borderRadius: '8px',
            border: `1px solid ${COLORS.border}`,
            background: isLightTheme(COLORS) ? '#ffffff' : '#071316',
            boxShadow: isLightTheme(COLORS) ? '0 12px 30px rgba(15,35,48,0.18)' : '0 12px 30px rgba(0,0,0,0.35)',
            color: COLORS.text,
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: 1.45,
            whiteSpace: 'pre-line',
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

function Gauge({ score, color, COLORS }) {
  const safeScore = Math.max(0, Math.min(100, Number(score) || 0));
  const angle = 180 + (safeScore / 100) * 180;
  const radius = 48;
  const center = 62;
  const x = center + Math.cos((angle * Math.PI) / 180) * radius;
  const y = center + Math.sin((angle * Math.PI) / 180) * radius;
  return (
    <svg viewBox="0 0 124 76" style={{ width: '150px', maxWidth: '100%' }} aria-hidden="true">
      <title>Der Zeiger zeigt die aktuelle Marktstimmung auf einer Skala von 0 bis 100. Links bedeutet Angst, rechts bedeutet Gier.</title>
      <path d="M 20 62 A 42 42 0 0 1 104 62" fill="none" stroke={trackBg(COLORS)} strokeWidth="8" strokeLinecap="round" />
      <path d="M 20 62 A 42 42 0 0 1 44 24" fill="none" stroke={COLORS.red} strokeWidth="8" strokeLinecap="round" />
      <path d="M 44 24 A 42 42 0 0 1 80 24" fill="none" stroke={COLORS.yellow || '#facc15'} strokeWidth="8" strokeLinecap="round" />
      <path d="M 80 24 A 42 42 0 0 1 104 62" fill="none" stroke={COLORS.green} strokeWidth="8" strokeLinecap="round" />
      <line x1={center} y1={center} x2={x} y2={y} stroke={color} strokeWidth="4" strokeLinecap="round" />
      <circle cx={center} cy={center} r="5" fill={color} />
    </svg>
  );
}

function SentimentChart({ history, color, COLORS }) {
  const [hover, setHover] = useState(null);
  const points = Array.isArray(history) ? history.slice(-90) : [];
  if (points.length < 2) {
    return (
      <div style={{ minHeight: '260px', display: 'grid', placeItems: 'center', color: COLORS.muted, fontSize: '13px' }}>
        Historie baut sich ab dem ersten Abruf auf.
      </div>
    );
  }
  const width = 720;
  const height = 260;
  const padding = { top: 26, right: 34, bottom: 32, left: 26 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const step = chartWidth / Math.max(1, points.length - 1);
  const yFor = value => padding.top + (100 - Math.max(0, Math.min(100, Number(value) || 0))) / 100 * chartHeight;
  const xFor = idx => padding.left + idx * step;
  const path = points.map((point, idx) => `${idx === 0 ? 'M' : 'L'} ${xFor(idx)} ${yFor(point.score)}`).join(' ');
  const area = `${path} L ${xFor(points.length - 1)} ${height - padding.bottom} L ${padding.left} ${height - padding.bottom} Z`;
  const last = points[points.length - 1];
  const hoverPoint = hover ? points[hover.idx] : null;
  const tickIndexes = Array.from(
    new Set([0, 1, 2, 3, 4].map(idx => Math.round(idx * (points.length - 1) / 4)))
  );
  const handleMove = event => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * width;
    const idx = Math.max(0, Math.min(points.length - 1, Math.round((x - padding.left) / step)));
    setHover({
      idx,
      x: xFor(idx),
      y: yFor(points[idx].score),
    });
  };
  const tooltipWidth = 268;
  const tooltipHeight = 72;
  const tooltipX = hover ? (hover.x > width - padding.right - tooltipWidth - 12 ? hover.x - tooltipWidth - 12 : hover.x + 12) : padding.left;
  const tooltipY = padding.top + 10;
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      style={{ width: '100%', minHeight: '260px', display: 'block', cursor: 'crosshair' }}
      role="img"
      aria-label="Marktstimmungs-Verlauf"
      onMouseMove={handleMove}
      onMouseLeave={() => setHover(null)}
    >
      {[20, 40, 60, 80].map(value => (
        <g key={value}>
          <line x1={padding.left} x2={width - padding.right} y1={yFor(value)} y2={yFor(value)} stroke={gridStroke(COLORS)} strokeDasharray="3 5" />
          <text x={width - 8} y={yFor(value) + 4} fill={COLORS.muted} fontSize="11" textAnchor="end">{value}</text>
        </g>
      ))}
      <rect x={padding.left} y={yFor(100)} width={chartWidth} height={yFor(75) - yFor(100)} fill={isLightTheme(COLORS) ? '#e8f4ec' : `${COLORS.green}18`} />
      <rect x={padding.left} y={yFor(25)} width={chartWidth} height={yFor(0) - yFor(25)} fill={isLightTheme(COLORS) ? '#f3e9df' : `${COLORS.red}18`} />
      <path d={area} fill={isLightTheme(COLORS) ? `${color}18` : `${color}22`} />
      <path d={path} fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={xFor(points.length - 1)} cy={yFor(last.score)} r="5" fill={color} />
      {tickIndexes.map(idx => (
        <g key={idx}>
          <line x1={xFor(idx)} x2={xFor(idx)} y1={height - padding.bottom} y2={height - padding.bottom + 5} stroke={COLORS.muted} opacity="0.5" />
          <text x={xFor(idx)} y={height - 8} fill={COLORS.muted} fontSize="10" textAnchor={idx === 0 ? 'start' : idx === points.length - 1 ? 'end' : 'middle'}>
            {formatMarketSignalChartDate(points[idx]?.date)}
          </text>
        </g>
      ))}
      {hoverPoint && (
        <g pointerEvents="none">
          <line x1={hover.x} x2={hover.x} y1={padding.top} y2={height - padding.bottom} stroke={COLORS.text} strokeWidth="1" opacity="0.25" />
          <circle cx={hover.x} cy={hover.y} r="5" fill={sentimentColor(hoverPoint.tone || _scoreTone(hoverPoint.score), COLORS)} stroke="#fff" strokeWidth="1.5" />
          <rect
            x={tooltipX}
            y={tooltipY}
            width={tooltipWidth}
            height={tooltipHeight}
            rx="10"
            fill={COLORS.card}
            stroke={COLORS.border}
            opacity="0.96"
          />
          <text x={tooltipX + 14} y={tooltipY + 24} fill={COLORS.text} fontSize="16" fontWeight="800">
            {formatMarketSignalChartDate(hoverPoint.date)}
          </text>
          <circle cx={tooltipX + 17} cy={tooltipY + 52} r="5" fill={sentimentColor(hoverPoint.tone || _scoreTone(hoverPoint.score), COLORS)} />
          <text x={tooltipX + 31} y={tooltipY + 57} fill={COLORS.text} fontSize="14">
            Marktstimmung: {hoverPoint.label} · {hoverPoint.score}
          </text>
        </g>
      )}
    </svg>
  );
}

function SentimentDistribution({ history, COLORS }) {
  const points = Array.isArray(history) ? history.slice(-90) : [];
  if (!points.length) return null;
  const buckets = [
    { key: 'extremeFear', label: 'Extrem', count: 0, color: COLORS.red },
    { key: 'fear', label: 'Angst', count: 0, color: COLORS.orange || '#f59e0b' },
    { key: 'neutral', label: 'Neutral', count: 0, color: COLORS.yellow || '#facc15' },
    { key: 'greed', label: 'Gier', count: 0, color: COLORS.green },
  ];
  points.forEach(point => {
    const score = Number(point.score) || 0;
    if (score <= 24) buckets[0].count += 1;
    else if (score <= 44) buckets[1].count += 1;
    else if (score <= 55) buckets[2].count += 1;
    else buckets[3].count += 1;
  });
  return (
    <div
      style={{ marginTop: '16px' }}
      title="Zeigt, wie oft die letzten Marktstimmungswerte in den jeweiligen Bereichen lagen."
    >
      <div style={{ fontWeight: 800, fontSize: '14px', marginBottom: '10px' }}>Stimmungsphasen der letzten 90 Werte</div>
      <div style={{ display: 'grid', gap: '8px' }}>
        {buckets.map(bucket => {
          const percent = Math.round((bucket.count / points.length) * 100);
          return (
            <div key={bucket.key} style={{ display: 'grid', gridTemplateColumns: '78px 1fr 42px', gap: '10px', alignItems: 'center', fontSize: '12px' }}>
              <span style={{ color: COLORS.muted }}>{bucket.label}</span>
              <div style={{ height: '8px', borderRadius: '999px', background: trackBg(COLORS), overflow: 'hidden' }}>
                <div style={{ width: `${percent}%`, height: '100%', background: bucket.color }} />
              </div>
              <span style={{ color: COLORS.text, fontWeight: 800, textAlign: 'right' }}>{percent}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SentimentGuide({ COLORS }) {
  return (
    <div
      style={{
        marginTop: '14px',
        padding: '12px',
        borderRadius: '10px',
        border: `1px solid ${COLORS.border}`,
        background: panelBg(COLORS),
        color: COLORS.muted,
        fontSize: '12px',
        lineHeight: 1.55,
      }}
    >
      <div style={{ fontWeight: 800, color: COLORS.text, marginBottom: '6px' }}>So liest du die Marktstimmung</div>
      <div>
        Niedrige Werte zeigen Vorsicht oder Stress im Markt, hohe Werte zeigen Risikoappetit. Der Indikator hilft, das Marktumfeld einzuordnen:
        Bei Angst werden Einstiege selektiver betrachtet, bei Gier steigt das Risiko von Übertreibungen. Die Marktstimmung ist ein Kontextfilter und ersetzt kein RSL-Signal.
      </div>
      <div style={{ marginTop: '10px', fontWeight: 800, color: COLORS.text }}>
        Wichtig: Die Marktstimmung ist kein Kauf- oder Verkaufssignal. Sie ist ein Kontextfilter: Sie zeigt, ob das Umfeld eher riskant, neutral oder freundlich ist. Entscheidungen im Scanner/Depot bleiben weiterhin regelbasiert über RSL und die jeweiligen Signale.
      </div>
    </div>
  );
}

const COMPONENT_TOOLTIPS = {
  momentum: 'Vergleicht den aktuellen Stand des S&P 500 gegen seinen 125-Tage-Durchschnitt (mit Gewichtung 55 %) sowie seinen 21-Tage-Durchschnitt (mit Gewichtung 45 %). Der Wert zeigt an, wie viel Prozent der S&P aktuell über/unter dem Durchschnitt notiert. Ein positiver Wert bedeutet eher Gier/Risikobereitschaft, ein negativer Wert bedeutet eher Angst/Sicherheitsbedürfnis.',
  volatility: 'Vergleicht den aktuellen Stand des VIX gegen seinen 50-Tage-Durchschnitt. Der Wert zeigt an, wie viel Prozent der VIX aktuell über/unter dem Durchschnitt notiert. Ein positiver Wert bedeutet eher Angst/Sicherheitsbedürfnis, ein negativer Wert bedeutet eher Gier/Risikobereitschaft.',
  safe_haven: 'Vergleicht die 20-Tage-Performance von Aktien (via SPY) mit der 20-Tage-Performance langlaufender US-Staatsanleihen (via TLT). Wenn Anleihen stärker nachgefragt sind, spricht das eher für Vorsicht. Ein positiver Wert bedeutet eher Gier/Risikobereitschaft, ein negativer Wert bedeutet eher Angst/Sicherheitsbedürfnis.',
  junk_bonds: 'Vergleicht die 20-Tage-Performance von High-Yield-Anleihen (via HYG) mit der 20-Tage-Performance von Investment-Grade-Anleihen (via LQD). Wenn Anleihen stärker nachgefragt sind, spricht das eher für Vorsicht. Ein positiver Wert bedeutet eher Gier/Risikobereitschaft, ein negativer Wert bedeutet eher Angst/Sicherheitsbedürfnis.',
  rsl_breadth: 'Zeigt die RSL-Stärke aller Aktien im S&P 500. Je mehr Aktien mit RSL-Wert > 1 bzw. „Halten“-Signal vorhanden sind, desto besser die Marktbreite und dementsprechend höher die angegebene Prozentzahl.',
};

function MarketSentimentPanel({ marketSentiment, COLORS, Card, Badge }) {
  if (!marketSentiment) return null;
  const score = Math.max(0, Math.min(100, Number(marketSentiment.score) || 0));
  const color = sentimentColor(marketSentiment.tone, COLORS);
  const components = Array.isArray(marketSentiment.components) ? marketSentiment.components : [];
  const history = Array.isArray(marketSentiment.history) ? marketSentiment.history : [];
  const previous = history.length > 1 ? history[history.length - 2] : null;
  const weekAgo = history.length > 0 ? history[Math.max(0, history.length - 8)] : null;
  const monthAgo = history.length > 0 ? history[Math.max(0, history.length - 30)] : null;

  return (
    <Card>
      <div style={{ marginBottom: '16px' }}>
        <h1 style={{ margin: '0 0 8px', fontSize: '24px', color: COLORS.text }}>Marktstimmung</h1>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
        <div style={{ display: 'grid', gap: '12px' }}>
          <div
            style={{ padding: '18px', borderRadius: '12px', border: `1px solid ${COLORS.border}`, background: panelBg(COLORS) }}
            title="Verdichteter Stimmungswert aus Momentum, Volatilität, Safe-Haven-Nachfrage, High-Yield-Nachfrage und RSL-Marktbreite."
          >
            <div style={{ fontWeight: 800, marginBottom: '12px' }}>
              Marktstimmungs-Index
              <InfoHint
                COLORS={COLORS}
                text="Der Gesamtwert von 0 bis 100 fasst mehrere Marktkomponenten zusammen. Niedrige Werte stehen für Angst, hohe Werte für Gier."
              />
            </div>
            <div style={{ display: 'grid', placeItems: 'center', gap: '4px' }}>
              <Gauge score={score} color={color} COLORS={COLORS} />
              <div style={{ fontSize: '38px', fontWeight: 900, color }}>{score}</div>
              <span title="Die Ampelfarbe ordnet den aktuellen Wert in Angst, Neutral oder Gier ein.">
                <Badge color={marketSentiment.tone === 'red' ? 'red' : marketSentiment.tone === 'orange' ? 'yellow' : marketSentiment.tone === 'yellow' ? 'yellow' : 'green'}>
                  {marketSentiment.label || 'Neutral'}
                </Badge>
              </span>
              <div style={{ color: COLORS.muted, fontSize: '12px', marginTop: '4px' }} title="Datenstand und Zeitpunkt der letzten Berechnung.">Stand {formatMarketSentimentStand(marketSentiment)}</div>
            </div>
          </div>
          <div
            style={{ padding: '18px', borderRadius: '12px', border: `1px solid ${COLORS.border}`, background: panelBg(COLORS) }}
            title="Vergleichswerte helfen einzuordnen, ob sich die Stimmung kurzfristig verbessert oder verschlechtert hat."
          >
            <div style={{ fontWeight: 800, marginBottom: '12px' }}>
              Historische Werte
              <InfoHint
                COLORS={COLORS}
                text="Zeigt, wie sich der aktuelle Stimmungswert gegenüber gestern, letzter Woche und letztem Monat verändert hat."
              />
            </div>
            {[
              ['Gestern', previous],
              ['Letzte Woche', weekAgo],
              ['Letzter Monat', monthAgo],
            ].map(([label, item]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ color: COLORS.muted, fontSize: '13px' }} title="Vergleichswert aus der gespeicherten Marktstimmungs-Historie.">{label}</span>
                <span style={{ fontWeight: 800, color: item ? sentimentColor(item.tone || _scoreTone(item.score), COLORS) : COLORS.muted }}>{item ? `${item.label || ''} ${item.score}` : '–'}</span>
              </div>
            ))}
          </div>
          <div style={{ padding: '18px', borderRadius: '12px', border: `1px solid ${COLORS.border}`, background: panelBg(COLORS) }}>
            <div style={{ fontWeight: 800, marginBottom: '12px' }}>
              Komponenten
              <InfoHint
                COLORS={COLORS}
                text="Die Bausteine zeigen, welche Marktbereiche den Gesamtwert aktuell eher belasten oder unterstützen."
              />
            </div>
            {components.length === 0 && (
              <div style={{ color: COLORS.muted, fontSize: '13px', lineHeight: 1.6 }}>
                Noch keine ausreichenden Daten für die Komponenten vorhanden.
              </div>
            )}
            {components.map(component => (
              <div key={component.key} style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '13px', marginBottom: '5px' }}>
                  <span style={{ color: COLORS.text, fontWeight: 700 }}>
                    {component.label}
                    <InfoHint
                      COLORS={COLORS}
                      text={COMPONENT_TOOLTIPS[component.key] || component.description || 'Teilkomponente des Marktstimmungswertes.'}
                    />
                  </span>
                  <span style={{ color: COLORS.muted }}>{formatSentimentValue(component)}</span>
                </div>
                <div
                  style={{ height: '7px', borderRadius: '999px', background: trackBg(COLORS), overflow: 'hidden' }}
                  title={`Komponenten-Score: ${Math.max(0, Math.min(100, Number(component.score) || 0))} von 100`}
                >
                  <div style={{ width: `${Math.max(0, Math.min(100, Number(component.score) || 0))}%`, height: '100%', background: sentimentColor(_scoreTone(component.score), COLORS) }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ padding: '18px', borderRadius: '12px', border: `1px solid ${COLORS.border}`, background: panelBg(COLORS), minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: '16px' }}>
                Marktstimmungs-Verlauf
                <InfoHint
                  COLORS={COLORS}
                  text="Zeigt die Entwicklung der Marktstimmung über die Zeit. Mit der Maus über die Linie fahren, um Datum und Wert zu sehen."
                />
              </div>
            </div>
          </div>
          <SentimentChart history={history.map(item => ({ ...item, tone: item.tone || _scoreTone(item.score) }))} color={color} COLORS={COLORS} />
          <SentimentDistribution history={history} COLORS={COLORS} />
          <SentimentGuide COLORS={COLORS} />
        </div>
      </div>
    </Card>
  );
}

function _scoreTone(score) {
  const value = Number(score) || 0;
  if (value <= 24) return 'red';
  if (value <= 44) return 'orange';
  if (value <= 55) return 'yellow';
  return 'green';
}

function signalToneColor(tone, COLORS) {
  if (tone === 'red') return COLORS.red;
  if (tone === 'yellow') return COLORS.yellow || '#f59e0b';
  if (tone === 'green') return COLORS.green;
  return COLORS.muted;
}

function signalBadgeColor(card) {
  if (card?.active) return 'red';
  if (card?.status === 'unknown') return 'yellow';
  return 'green';
}

function formatSignalValue(card) {
  if (!card || card.value === null || card.value === undefined) return '–';
  const value = Number(card.value);
  const formatted = Number.isFinite(value)
    ? value.toLocaleString('de-DE', { maximumFractionDigits: 2 })
    : String(card.value);
  return `${formatted}${card.unit ? ` ${card.unit}` : ''}`;
}

function marketSignalTitle(card) {
  const titles = {
    vix_20: 'VIX > 20',
    vix_sma200: 'VIX > SMA 200',
    vvix_105: 'VVIX > 105',
  };
  return titles[card?.key] || card?.title || '';
}

function marketSignalValueFontSize(card) {
  if (card?.key === 'es_crash') return 'clamp(16px, 1.45vw, 21px)';
  if (card?.key === 'vix_sma200') return 'clamp(16px, 1.5vw, 22px)';
  return 'clamp(17px, 1.7vw, 24px)';
}

function marketSignalTooltip(card) {
  const parts = [];
  const threshold = card?.threshold_label || card?.threshold;
  if (threshold) {
    parts.push(`Schwelle: ${threshold}`);
  }
  if (card?.details) parts.push(card.details);
  return parts.join('\n\n') || 'Automatisches Marktsignal mit aktueller Bewertung und optionalem E-Mail-Alarm.';
}

function formatMarketSignalChartValue(value, unit) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '–';
  return `${number.toLocaleString('de-DE', { maximumFractionDigits: 2 })}${unit ? ` ${unit}` : ''}`;
}

function formatMarketSignalAxisValue(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '–';
  return number.toLocaleString('de-DE', { maximumFractionDigits: 2 });
}

function formatMarketSignalChartDate(value) {
  if (!value) return '–';
  const raw = String(value).slice(0, 10);
  const date = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

function MarketSignalChart({ card, config, COLORS }) {
  const [hover, setHover] = useState(null);
  const points = (Array.isArray(card?.history) ? card.history : [])
    .map(item => ({
      ...item,
      value: Number(item.value),
    }))
    .filter(item => item.date && Number.isFinite(item.value))
    .slice(-120);

  if (points.length < 2) {
    return (
      <div
        style={{
          minHeight: '240px',
          display: 'grid',
          placeItems: 'center',
          borderRadius: '12px',
          border: `1px solid ${COLORS.border}`,
          background: chartBg(COLORS),
          color: COLORS.muted,
          fontSize: '13px',
          textAlign: 'center',
          padding: '18px',
        }}
      >
        Der Verlauf baut sich ab den nächsten automatischen Prüfungen auf.
      </div>
    );
  }

  const width = 760;
  const height = 250;
  const paddingLeft = 34;
  const paddingRight = 86;
  const paddingTop = 30;
  const paddingBottom = 32;
  const rawValues = points.map(point => point.value);
  const referenceValues = [config?.activeThreshold, config?.resetThreshold].filter(value => Number.isFinite(Number(value))).map(Number);
  const minRaw = Math.min(...rawValues, ...referenceValues);
  const maxRaw = Math.max(...rawValues, ...referenceValues);
  const range = Math.max(1, maxRaw - minRaw);
  const min = minRaw - range * 0.16;
  const max = maxRaw + range * 0.16;
  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;
  const step = chartWidth / Math.max(1, points.length - 1);
  const yFor = value => paddingTop + ((max - value) / (max - min)) * chartHeight;
  const xFor = idx => paddingLeft + idx * step;
  const path = points.map((point, idx) => `${idx === 0 ? 'M' : 'L'} ${xFor(idx)} ${yFor(point.value)}`).join(' ');
  const color = card?.active ? COLORS.red : signalToneColor(card?.status_tone, COLORS);
  const tickIndexes = Array.from(
    new Set([0, 1, 2, 3, 4].map(idx => Math.round(idx * (points.length - 1) / 4)))
  );
  const gridValues = [0, 1, 2, 3].map(idx => min + (idx * (max - min)) / 3);
  const hoverPoint = hover ? points[hover.idx] : null;
  const handleMove = event => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * width;
    const idx = Math.max(0, Math.min(points.length - 1, Math.round((x - paddingLeft) / step)));
    setHover({ idx, x: xFor(idx), y: yFor(points[idx].value) });
  };
  const thresholdLines = [
    { key: 'active', value: config?.activeThreshold, label: 'Stress', color: COLORS.red },
    { key: 'reset', value: config?.resetThreshold, label: 'Entspannt', color: COLORS.green },
  ].filter(item => Number.isFinite(Number(item.value)) && Number(item.value) >= min && Number(item.value) <= max);
  const tooltipWidth = 166;
  const tooltipHeight = 48;
  const tooltipX = hover ? (hover.x > width - paddingRight - tooltipWidth - 12 ? hover.x - tooltipWidth - 12 : hover.x + 12) : paddingLeft;
  const tooltipY = paddingTop + 10;

  return (
    <>
    <svg
      viewBox={`0 0 ${width} ${height}`}
      style={{
        width: '100%',
        minHeight: '250px',
        display: 'block',
        borderRadius: '12px',
        border: `1px solid ${COLORS.border}`,
        background: chartBg(COLORS),
        cursor: 'crosshair',
      }}
      role="img"
      aria-label={`${card?.title || 'Marktsignal'} Verlauf`}
      onMouseMove={handleMove}
      onMouseLeave={() => setHover(null)}
    >
      {gridValues.map(value => (
        <g key={value}>
          <line x1={paddingLeft} x2={width - paddingRight} y1={yFor(value)} y2={yFor(value)} stroke={weakGridStroke(COLORS)} strokeDasharray="3 5" />
          <text x={width - 8} y={yFor(value) + 4} fill={COLORS.muted} fontSize="10" textAnchor="end">
            {formatMarketSignalAxisValue(value)}
          </text>
        </g>
      ))}
      {thresholdLines.map(line => {
        const y = yFor(Number(line.value));
        return (
          <g key={line.key}>
            <line x1={paddingLeft} x2={width - paddingRight} y1={y} y2={y} stroke={line.color} strokeDasharray="7 5" opacity="0.75" />
          </g>
        );
      })}
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((point, idx) => (
        point.active ? <circle key={`${point.date}-${idx}`} cx={xFor(idx)} cy={yFor(point.value)} r="2.5" fill={COLORS.red} /> : null
      ))}
      {tickIndexes.map(idx => (
        <g key={idx}>
          <line x1={xFor(idx)} x2={xFor(idx)} y1={height - paddingBottom} y2={height - paddingBottom + 5} stroke={COLORS.muted} opacity="0.5" />
          <text x={xFor(idx)} y={height - 8} fill={COLORS.muted} fontSize="10" textAnchor={idx === 0 ? 'start' : idx === points.length - 1 ? 'end' : 'middle'}>
            {formatMarketSignalChartDate(points[idx]?.date)}
          </text>
        </g>
      ))}
      {hoverPoint && (
        <g pointerEvents="none">
          <line x1={hover.x} x2={hover.x} y1={paddingTop} y2={height - paddingBottom} stroke={COLORS.text} strokeWidth="1" opacity="0.25" />
          <circle cx={hover.x} cy={hover.y} r="5" fill={hoverPoint.active ? COLORS.red : color} stroke="#fff" strokeWidth="1.5" />
          <rect
            x={tooltipX}
            y={tooltipY}
            width={tooltipWidth}
            height={tooltipHeight}
            rx="10"
            fill={COLORS.card}
            stroke={COLORS.border}
            opacity="0.96"
          />
          <text x={tooltipX + 10} y={tooltipY + 17} fill={COLORS.text} fontSize="8.5" fontWeight="800">
            {formatMarketSignalChartDate(hoverPoint.date)}
          </text>
          <circle cx={tooltipX + 13} cy={tooltipY + 34} r="3.2" fill={hoverPoint.active ? COLORS.red : color} />
          <text x={tooltipX + 22} y={tooltipY + 37} fill={COLORS.text} fontSize="7.6">
            {card?.title || 'Signal'}: {formatMarketSignalChartValue(hoverPoint.value, card?.unit)}
          </text>
        </g>
      )}
    </svg>
    {thresholdLines.length > 0 && (
      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginTop: '8px', color: COLORS.muted, fontSize: '12px', fontWeight: 800 }}>
        {thresholdLines.map(line => (
          <span key={line.key} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
            <span
              aria-hidden="true"
              style={{
                width: '24px',
                borderTop: `2px dashed ${line.color}`,
                opacity: 0.8,
              }}
            />
            {line.label}
          </span>
        ))}
      </div>
    )}
    </>
  );
}

const MARKET_SIGNAL_CHARTS = {
  vix_20: {
    label: 'VIX',
    activeThreshold: 20,
    resetThreshold: 19,
    activeLabel: 'Aktiv > 20',
    resetLabel: 'Entspannung < 19',
  },
  vvix_105: {
    label: 'VVIX',
    activeThreshold: 105,
    resetThreshold: 100,
    activeLabel: 'Aktiv > 105',
    resetLabel: 'Entspannung < 100',
  },
  vix_sma200: {
    label: 'Abstand zum SMA 200',
    activeThreshold: 0,
    resetThreshold: null,
    activeLabel: 'VIX SMA 200 bei 0%',
    note: 'Der Verlauf zeigt den prozentualen Abstand des VIX zum SMA 200 im Daily-Chart. Über 0% liegt der VIX über dem SMA 200 und das Signal zeigt Stress.',
  },
  es_crash: {
    label: 'Abstand zur EMA 21',
    activeThreshold: 0,
    resetThreshold: null,
    activeLabel: 'Weekly EMA 21 bei 0%',
    note: 'Der Verlauf zeigt den prozentualen Abstand des ES-Weekly-Close zur EMA 21. Unter 0% liegt der ES unter der EMA 21 und das Signal ist aktiv.',
  },
};

function MarketSignalCards({
  cards,
  onToggle,
  onBlockedAlert,
  canUseMarketSignalEmailAlerts,
  message,
  error,
  COLORS,
  Card,
  Badge,
}) {
  const entries = Array.isArray(cards) ? cards : [];
  const [openChartKey, setOpenChartKey] = useState('');
  if (entries.length === 0) return null;
  const openChartCard = entries.find(card => card.key === openChartKey);
  const openChartConfig = openChartCard ? MARKET_SIGNAL_CHARTS[openChartCard.key] : null;

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '14px' }}>
        <div>
          <div style={{ fontSize: '18px', fontWeight: 900, color: COLORS.text }}>Automatische Marktsignale</div>
          <div style={{ color: COLORS.muted, fontSize: '13px', lineHeight: 1.5, marginTop: '4px' }}>
            Aktualisierung: alle 20 Minuten automatisch; beim Öffnen des Signal-Moduls wird der aktuelle Stand zusätzlich geladen.
          </div>
        </div>
      </div>
      {error && <div style={{ color: COLORS.red, fontSize: '13px', margin: '-4px 0 14px' }}>⚠ {error}</div>}
      {message && <div style={{ color: COLORS.green, fontSize: '13px', margin: '-4px 0 14px' }}>✓ {message}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px' }}>
        {entries.map(card => {
          const color = signalToneColor(card.status_tone, COLORS);
          const canEditAlert = canUseMarketSignalEmailAlerts || !!card.alert_available || !!card.alert_enabled;
          return (
            <div
              key={card.key}
              style={{
                minHeight: '178px',
                display: 'grid',
                gridTemplateRows: 'auto 1fr auto',
                gap: '12px',
                padding: '16px',
                borderRadius: '12px',
                border: `1px solid ${card.active ? `${COLORS.red}66` : COLORS.border}`,
                background: card.active ? (isLightTheme(COLORS) ? '#fff1f2' : '#7f1d1d22') : panelBg(COLORS),
                boxSizing: 'border-box',
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: '8px', alignItems: 'flex-start', minWidth: 0 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 900, color: COLORS.text, whiteSpace: 'nowrap', fontSize: card.key === 'es_crash' ? '13px' : '14px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <span>{marketSignalTitle(card)}</span>
                    <InfoHint COLORS={COLORS} text={marketSignalTooltip(card)} />
                  </div>
                  <div style={{ color: COLORS.muted, fontSize: '11px', lineHeight: 1.35, marginTop: '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {card.subtitle || card.description || ''}
                  </div>
                </div>
                <span style={{ minWidth: 0, justifySelf: 'end' }}>
                  <Badge color={signalBadgeColor(card)}>{card.status_label || 'OK'}</Badge>
                </span>
              </div>

              <div>
                <div style={{ color, fontSize: marketSignalValueFontSize(card), fontWeight: 900, lineHeight: 1.05, whiteSpace: 'nowrap', letterSpacing: 0 }}>{formatSignalValue(card)}</div>
                <div style={{ color: COLORS.muted, fontSize: '12px', lineHeight: 1.5, marginTop: '8px' }}>
                  Stand {card.as_of || '–'}
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto',
                  alignItems: 'center',
                  gap: '8px',
                  paddingTop: '10px',
                  borderTop: `1px solid ${COLORS.border}`,
                  color: COLORS.text,
                  fontSize: '12px',
                  fontWeight: 800,
                  userSelect: 'none',
                  minWidth: 0,
                }}
              >
                <label
                  onClick={event => {
                    if (!canEditAlert) {
                      event.preventDefault();
                      onBlockedAlert?.();
                    }
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: canEditAlert ? 'pointer' : 'not-allowed',
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    opacity: canEditAlert ? 1 : 0.68,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!card.alert_enabled}
                    disabled={!canEditAlert}
                    onChange={event => onToggle(card.key, event.target.checked)}
                    style={{ width: '16px', height: '16px', accentColor: COLORS.accent }}
                  />
                  Alarm per E-Mail
                </label>
                <button
                  type="button"
                  onClick={event => {
                    event.preventDefault();
                    setOpenChartKey(openChartKey === card.key ? '' : card.key);
                  }}
                  style={{
                    minHeight: '32px',
                    padding: '0 10px',
                    borderRadius: '9px',
                    border: `1px solid ${openChartKey === card.key ? COLORS.cyan : COLORS.border}`,
                    background: openChartKey === card.key ? `${COLORS.cyan}22` : (isLightTheme(COLORS) ? '#ffffff' : '#ffffff08'),
                    color: openChartKey === card.key ? COLORS.cyan : COLORS.text,
                    fontWeight: 900,
                    fontSize: '12px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    boxSizing: 'border-box',
                    maxWidth: '58px',
                  }}
                >
                  Chart
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {openChartCard && openChartConfig && (
        <div style={{ marginTop: '14px' }}>
          <div style={{ color: COLORS.text, fontWeight: 900, fontSize: '14px', marginBottom: '8px' }}>
            {openChartCard.title} Chart
            {openChartConfig.label ? (
              <span style={{ color: COLORS.muted, fontWeight: 700 }}> · {openChartConfig.label}</span>
            ) : null}
          </div>
          {openChartConfig.note && (
            <div style={{ color: COLORS.muted, fontSize: '12px', lineHeight: 1.5, marginBottom: '8px' }}>
              {openChartConfig.note}
            </div>
          )}
          <MarketSignalChart card={openChartCard} config={openChartConfig} COLORS={COLORS} />
        </div>
      )}
    </Card>
  );
}

export default function SignalsSection({
  isAdmin,
  COLORS,
  inputStyle,
  signalsLoading,
  signalsEntries,
  marketSentiment,
  marketSignalCards,
  signalsErr,
  signalsMsg,
  marketSignalsErr,
  marketSignalsMsg,
  signalEditId,
  signalKind,
  setSignalKind,
  signalText,
  setSignalText,
  signalRelatedId,
  setSignalRelatedId,
  signalFilter,
  setSignalFilter,
  signalViewMode,
  setSignalViewMode,
  signalOpenLimit,
  setSignalOpenLimit,
  signalCloseLimit,
  setSignalCloseLimit,
  SIGNAL_KINDS,
  offeneSignals,
  openSignals,
  geschlosseneOpenSignals,
  geschlosseneSignals,
  sichtbareOpenSignals,
  sichtbareGeschlosseneSignals,
  closedSignalIds,
  signalMap,
  ladeSignale,
  signalSpeichern,
  marketSignalAlarmAendern,
  onBlockedMarketSignalAlarm,
  canUseMarketSignalEmailAlerts,
  signalBearbeiten,
  signalLoeschen,
  setSignalEditId,
  setSignalsErr,
  setSignalsMsg,
}) {
  const { Card, Btn, Badge } = useMemo(() => buildUiHelpers({ COLORS, th: {} }), [COLORS]);

  return (
    <div>
      <div style={{ display: 'grid', gap: '20px' }}>
        <MarketSentimentPanel marketSentiment={marketSentiment} COLORS={COLORS} Card={Card} Badge={Badge} />
        <MarketSignalCards
          cards={marketSignalCards}
          onToggle={marketSignalAlarmAendern}
          onBlockedAlert={onBlockedMarketSignalAlarm}
          canUseMarketSignalEmailAlerts={canUseMarketSignalEmailAlerts}
          message={marketSignalsMsg}
          error={marketSignalsErr}
          COLORS={COLORS}
          Card={Card}
          Badge={Badge}
        />

        {isAdmin && (
          <Card>
            <div style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px' }}>
              {signalEditId ? 'Signal bearbeiten' : 'Neues Signal eingeben'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 220px auto', gap: '10px', alignItems: 'end' }}>
              <div>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Typ</label>
                <select
                  style={{ ...inputStyle, marginBottom: 0, appearance: 'none' }}
                  value={signalKind}
                  onChange={e => setSignalKind(e.target.value)}
                  disabled={!!signalEditId}
                >
                  {SIGNAL_KINDS.map(kind => (
                    <option key={kind} value={kind}>{kind.toUpperCase()}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Signaltext</label>
                <input
                  style={{ ...inputStyle, marginBottom: 0 }}
                  autoComplete="off"
                  value={signalText}
                  onChange={e => setSignalText(e.target.value)}
                  placeholder={signalKind === 'open' ? 'Open Signal eingeben' : 'Close Signal eingeben'}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Bezieht sich auf</label>
                <select
                  style={{ ...inputStyle, marginBottom: 0, appearance: 'none' }}
                  value={signalRelatedId}
                  onChange={e => setSignalRelatedId(e.target.value)}
                  disabled={signalKind !== 'close' || !!signalEditId}
                >
                  <option value="">Kein Bezug</option>
                  {offeneSignals.map(entry => (
                    <option key={entry.id} value={entry.id}>
                      {new Date(entry.created_at).toLocaleString('de-DE')} - {entry.text.slice(0, 48)}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Btn onClick={signalSpeichern} color="green" size="sm">{signalEditId ? 'Ändern' : 'Speichern'}</Btn>
                {signalEditId && (
                  <Btn
                    onClick={() => {
                      setSignalEditId(null);
                      setSignalKind('open');
                      setSignalText('');
                      setSignalRelatedId('');
                      setSignalsErr('');
                      setSignalsMsg('');
                    }}
                    color="ghost"
                    size="sm"
                  >
                    Abbrechen
                  </Btn>
                )}
              </div>
            </div>
            {signalsErr && <div style={{ color: COLORS.red, fontSize: '13px', marginTop: '12px' }}>⚠ {signalsErr}</div>}
            {signalsMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginTop: '12px' }}>✓ {signalsMsg}</div>}
          </Card>
        )}

        {isAdmin && (
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <div style={{ fontSize: '16px', fontWeight: '700' }}>Signalverlauf</div>
              <Btn onClick={ladeSignale} color="ghost" size="sm">Aktualisieren</Btn>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'end', flexWrap: 'wrap', marginBottom: '14px' }}>
              <div style={{ flex: '1 1 280px' }}>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>Suche / Filter</label>
                <input
                  style={{ ...inputStyle, marginBottom: 0 }}
                  autoComplete="off"
                  value={signalFilter}
                  onChange={e => setSignalFilter(e.target.value)}
                  placeholder="Nach Signaltext suchen"
                />
              </div>
              {signalFilter && <Btn onClick={() => setSignalFilter('')} color="ghost" size="sm">Filter löschen</Btn>}
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
              {[
                { id: 'all', label: 'Alle' },
                { id: 'open', label: 'Nur offene' },
                { id: 'close', label: 'Nur geschlossene' },
              ].map(mode => (
                <button
                  key={mode.id}
                  onClick={() => setSignalViewMode(mode.id)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '999px',
                    border: `1px solid ${signalViewMode === mode.id ? COLORS.accent : COLORS.border}`,
                    background: signalViewMode === mode.id ? COLORS.accent : 'transparent',
                    color: signalViewMode === mode.id ? '#fff' : COLORS.muted,
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '700',
                  }}
                >
                  {mode.label}
                </button>
              ))}
            </div>

            {signalsLoading && <div style={{ color: COLORS.muted, fontSize: '14px' }}>Lade Signale...</div>}
            {!signalsLoading && signalsEntries.length === 0 && <div style={{ color: COLORS.muted, fontSize: '14px' }}>Noch keine exklusiven Signale vorhanden.</div>}
            {!signalsLoading && signalsEntries.length > 0 && openSignals.length === 0 && geschlosseneSignals.length === 0 && (
              <div style={{ color: COLORS.muted, fontSize: '14px' }}>Keine Signale passend zum aktuellen Filter.</div>
            )}

            {!signalsLoading && signalsEntries.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
                {signalViewMode !== 'close' && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                      <div style={{ fontSize: '15px', fontWeight: '700' }}>Open-Signale</div>
                      <Badge color="green">{openSignals.length}</Badge>
                      {geschlosseneOpenSignals.length > 0 && <Badge color="yellow">{geschlosseneOpenSignals.length} geschlossen</Badge>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {openSignals.length === 0 && <div style={{ color: COLORS.muted, fontSize: '13px' }}>Aktuell keine Open-Signale passend zum Filter.</div>}
                      {sichtbareOpenSignals.map(entry => (
                        <div
                          key={entry.id}
                          style={{
                            padding: '14px 16px',
                            borderRadius: '12px',
                            background: closedSignalIds.has(entry.id) ? '#a1620718' : '#14532d22',
                            border: `1px solid ${closedSignalIds.has(entry.id) ? COLORS.yellow + '44' : COLORS.green + '44'}`,
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              <Badge color="green">OPEN</Badge>
                              <Badge color={closedSignalIds.has(entry.id) ? 'yellow' : 'green'}>
                                {closedSignalIds.has(entry.id) ? 'geschlossen' : 'aktiv'}
                              </Badge>
                            </div>
                            <div style={{ fontSize: '12px', color: COLORS.muted }}>{new Date(entry.created_at).toLocaleString('de-DE')}</div>
                          </div>
                          <div style={{ color: COLORS.text, fontSize: '14px', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{entry.text}</div>
                          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                            <Btn onClick={() => signalBearbeiten(entry)} color="ghost" size="sm">Bearbeiten</Btn>
                            <Btn onClick={() => signalLoeschen(entry.id)} color="red" size="sm">Löschen</Btn>
                          </div>
                        </div>
                      ))}
                      {openSignals.length > signalOpenLimit && <Btn onClick={() => setSignalOpenLimit(limit => limit + 8)} color="ghost" size="sm">Mehr laden</Btn>}
                      {signalOpenLimit > 8 && openSignals.length > 8 && <Btn onClick={() => setSignalOpenLimit(8)} color="ghost" size="sm">Weniger anzeigen</Btn>}
                    </div>
                  </div>
                )}

                {signalViewMode !== 'open' && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                      <div style={{ fontSize: '15px', fontWeight: '700' }}>Geschlossene Signale</div>
                      <Badge color="red">{geschlosseneSignals.length}</Badge>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {geschlosseneSignals.length === 0 && <div style={{ color: COLORS.muted, fontSize: '13px' }}>Noch keine geschlossenen Signale.</div>}
                      {sichtbareGeschlosseneSignals.map(entry => {
                        const bezug = entry.related_signal_id ? signalMap[entry.related_signal_id] : null;
                        return (
                          <div
                            key={entry.id}
                            style={{
                              padding: '14px 16px',
                              borderRadius: '12px',
                              background: '#7f1d1d22',
                              border: `1px solid ${COLORS.red}44`,
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                <Badge color="red">CLOSE</Badge>
                                {bezug && <Badge color="ghost">zu OPEN</Badge>}
                              </div>
                              <div style={{ fontSize: '12px', color: COLORS.muted }}>{new Date(entry.created_at).toLocaleString('de-DE')}</div>
                            </div>
                            {bezug && (
                              <div style={{
                                marginBottom: '10px',
                                padding: '8px 10px',
                                borderRadius: '10px',
                                background: '#ffffff08',
                                border: `1px solid ${COLORS.border}`,
                                color: COLORS.muted,
                                fontSize: '12px',
                                lineHeight: 1.6,
                              }}>
                                <div style={{ fontWeight: '700', color: COLORS.text, marginBottom: '4px' }}>Bezogenes Open-Signal</div>
                                <div>{bezug.text}</div>
                              </div>
                            )}
                            <div style={{ color: COLORS.text, fontSize: '14px', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{entry.text}</div>
                            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                              <Btn onClick={() => signalBearbeiten(entry)} color="ghost" size="sm">Bearbeiten</Btn>
                              <Btn onClick={() => signalLoeschen(entry.id)} color="red" size="sm">Löschen</Btn>
                            </div>
                          </div>
                        );
                      })}
                      {geschlosseneSignals.length > signalCloseLimit && <Btn onClick={() => setSignalCloseLimit(limit => limit + 8)} color="ghost" size="sm">Mehr laden</Btn>}
                      {signalCloseLimit > 8 && geschlosseneSignals.length > 8 && <Btn onClick={() => setSignalCloseLimit(8)} color="ghost" size="sm">Weniger anzeigen</Btn>}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
